import { useEffect, useState } from 'react';
import { api } from '@/lib/api';

// `GET /api/` carries the deployment's capability flags (booleans only, never
// credentials). Cached module-wide as a promise so the several places that need
// a flag share one request per page load.
let metaPromise = null;

function loadMeta() {
  if (!metaPromise) {
    metaPromise = api.get('/')
      .then((r) => r.data || {})
      // Unreachable API: report nothing as available. Claiming a channel works
      // when we can't confirm it is the failure mode this exists to prevent.
      .catch(() => ({}));
  }
  return metaPromise;
}

/**
 * Whether Fax is a real delivery channel on this deployment (GAPS R6).
 *
 * Fax is the one channel that transmits the signed packet itself, so it stays
 * off until a provider is configured AND its BAA is confirmed. Staff must not be
 * offered a channel that silently goes nowhere, hence `reason` — wording an
 * admin can act on rather than a bare disabled control.
 */
export function useFaxStatus() {
  const [status, setStatus] = useState({ enabled: false, reason: '', loaded: false });
  useEffect(() => {
    let alive = true;
    loadMeta().then((m) => {
      if (!alive) return;
      setStatus({
        enabled: !!m.fax_enabled,
        reason: m.fax_unavailable_reason || 'Fax delivery is not available on this deployment',
        loaded: true,
      });
    });
    return () => { alive = false; };
  }, []);
  return status;
}

/**
 * Whether an AI provider is configured on this deployment (§2 admin note,
 * full-flow review 2026-09-06).
 *
 * `ai_mode` defaults to `real`, and with no provider every AI feature quietly
 * degrades to keyword matching and template text. The practice was never told,
 * so the admin dashboard and the AI settings card say so. `loaded` lets a page
 * hold the banner until the answer is in rather than flashing it on every load.
 */
export function useAiStatus() {
  const [status, setStatus] = useState({ configured: true, loaded: false });
  useEffect(() => {
    let alive = true;
    loadMeta().then((m) => {
      if (!alive) return;
      // An unreachable API says nothing: `configured` stays true so we do not
      // accuse a working deployment of being unconfigured over a network blip.
      setStatus({ configured: m.ai_configured === undefined ? true : !!m.ai_configured, loaded: true });
    });
    return () => { alive = false; };
  }, []);
  return status;
}
